import dayjs from "dayjs";
import da from "dayjs/locale/da";
import { CATEGORY_LABELS, formatKr } from "../../../finance/ledger";
import { FinanceTransaction, LogeEvent, LogeMember } from "../../../types/entities";
import { Icon } from "../Icon";
import { CATEGORY_ICONS } from "./categoryMeta";

dayjs.locale(da);

/**
 * One movement in the ledger.
 *
 * Deliberately quiet. The list is the receipts drawer under the chart, not
 * the point of the screen — so a row states the amount plainly and keeps its
 * colour to the number itself, rather than tinting the whole row the way the
 * fines list does. Money in is mint, money out is ember, and everything else
 * on the row is the same two greys.
 */
export function TransactionRow({
  transaction,
  member,
  event,
  balanceOre,
  onClick,
}: {
  transaction: FinanceTransaction;
  /** The member it concerns — arrangør, or whoever paid. */
  member?: LogeMember;
  /** The samling it paid for. */
  event?: LogeEvent;
  /** The balance once this had happened. Shown under the amount where the
   *  row is the whole ledger rather than a recent-movements teaser. */
  balanceOre?: number;
  onClick?: () => void;
}) {
  const income = transaction.amount_ore >= 0;
  const label = CATEGORY_LABELS[transaction.category];
  const note = transaction.note?.trim();
  const title = note || label;

  // The attribution line, in the order you'd read it out: when, what kind,
  // who/what it was for. Anything missing drops out — and so does the
  // category when the note already said it, which most of the imported rows
  // do ("Arrangement", booked as an arrangement). A row that reads
  // "Arrangement · arrangement" spends its second line saying nothing.
  const meta = [
    dayjs(transaction.occurred_on).format("D. MMM"),
    title.toLowerCase() === label.toLowerCase() ? null : label,
    event?.title,
    member?.name,
  ].filter(Boolean);

  const Wrapper = onClick ? "button" : "div";
  return (
    <Wrapper
      onClick={onClick}
      className={`w-full text-left flex items-center gap-2.5 px-3.5 py-2.5 ${
        onClick ? "active:bg-white/[0.04]" : ""
      }`}
    >
      <div
        className={`w-8 h-8 rounded flex-shrink-0 flex items-center justify-center ${
          income ? "bg-mint/10 text-mint" : "bg-ember/10 text-ember"
        }`}
      >
        <Icon name={CATEGORY_ICONS[transaction.category]} size={12} />
      </div>

      <div className="flex-1 min-w-0">
        <div className="font-display text-[13.5px] font-bold uppercase tracking-tight text-vault-ink truncate">
          {title}
        </div>
        <div className="font-mono text-[9px] tracking-[0.8px] uppercase text-vault-ink3 mt-0.5 truncate">
          {meta.join(" · ")}
        </div>
      </div>

      <div className="flex-shrink-0 text-right">
        <div
          className={`font-mono text-[12.5px] font-semibold tabular-nums ${
            income ? "text-mint" : "text-ember"
          }`}
        >
          {formatKr(transaction.amount_ore, { signed: true, prefix: false })}
        </div>
        {balanceOre != null && (
          <div className="font-mono text-[9px] tracking-[0.6px] tabular-nums text-vault-ink3 mt-0.5">
            {formatKr(balanceOre, { prefix: false })}
          </div>
        )}
      </div>
      {onClick && <Icon name="chevron-right" size={9} className="text-vault-ink3 flex-shrink-0" />}
    </Wrapper>
  );
}
